"use client";

import { useCallback, useMemo } from "react";
import { useTableRowActionContext } from "@/lib/hooks/ui/useTableRowActionContext";
import { useActionFiltering } from "@/lib/hooks/search/useActionFiltering";
import { executeDetailAction } from "@/components/shared/UniversalDetailPage/actions/execute-action";
import {
  getActionCategory,
  partitionByCategory,
  sortActionsByCategory,
  type ActionCategory,
} from "@/lib/actions/action-categories";
import type {
  DetailAction,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type {
  EntityHooks,
  SimpleTableConfig,
} from "@/components/shared/UniversalCRMTable/types/shared";
import type { RowActionRoleFilter } from "@/components/shared/UniversalCRMTable/contexts/SimpleTableRowServicesContext";

export interface RowActionItem<T extends BaseEntity> {
  action: DetailAction<T>;
  label: string;
  isDisabled: boolean;
  isDestructive: boolean;
  /** Priority bucket — consumers insert a divider when this changes. */
  category: ActionCategory;
  run: () => void;
}

// The pure grouping helper lives in its own import-light module so it can be
// unit-tested and shared without dragging in the action-execution graph.
export { groupRowActionItems, type RowActionItemGroup } from "./row-action-groups";

interface UseRowActionItemsParams<T extends BaseEntity> {
  item: T;
  actions: DetailAction<T>[];
  hooks: EntityHooks<T>;
  entityName: string;
  config?: SimpleTableConfig<T>;
}

export interface RowActionItemsResult<T extends BaseEntity> {
  ready: boolean;
  items: RowActionItem<T>[];
  partition: { top: RowActionItem<T>[]; more: RowActionItem<T>[]; danger: RowActionItem<T>[] };
}

/**
 * Shared source of truth for an entity row's actions — the exact same
 * role-filtering + execution path used by the desktop right-click context menu
 * and dropdown. Consumed by both `RowActionsDropdownMenu` (desktop dropdown) and
 * `MobileRowActionsSheet` (mobile bottom sheet) so the two never diverge.
 *
 * This variant SUBSCRIBES: it calls `hooks.useUpdate()`, `hooks.useDelete()` and
 * `useActionFiltering()` itself, which is right for a standalone surface (a
 * drilldown, a detail panel — see ENTITY_TABLE_ROW_ACTIONS) but costs four
 * subscriptions per row inside a table. Inside a `SimpleTable` the caller reads
 * `useSimpleTableRowServices()` and uses `useRowActionItemsWithServices` instead.
 */
export function useRowActionItems<T extends BaseEntity>(
  params: UseRowActionItemsParams<T>
): RowActionItemsResult<T> {
  const updateMutation = params.hooks.useUpdate();
  const deleteMutation = params.hooks.useDelete();
  const { filterActionsByRole } = useActionFiltering();

  return useRowActionItemsWithServices({
    ...params,
    updateMutation,
    deleteMutation,
    filterActionsByRole,
  });
}

/**
 * The same action list built from services the CALLER already holds — the one
 * table-level `useUpdate`/`useDelete`/`useActionFiltering` subscription hoisted
 * into `SimpleTableRowServicesContext`. Mirrors the
 * `TableRowActionsBody` / `TableRowActionsWrapper` split on the desktop row.
 */
export function useRowActionItemsWithServices<T extends BaseEntity>({
  item,
  actions,
  hooks,
  entityName,
  config,
  updateMutation,
  deleteMutation,
  filterActionsByRole,
}: UseRowActionItemsParams<T> & {
  updateMutation: ReturnType<EntityHooks<T>["useUpdate"]>;
  deleteMutation: ReturnType<EntityHooks<T>["useDelete"]>;
  filterActionsByRole: RowActionRoleFilter;
}): RowActionItemsResult<T> {
  const refetch = useCallback(() => Promise.resolve(), []);

  const rowActionContext = useTableRowActionContext({
    item,
    hooks,
    refetch,
    updateMutation,
    deleteMutation,
    entityName,
    config,
  });

  const visibleActions = useMemo(() => {
    const roleFilteredActions = filterActionsByRole(actions);
    return roleFilteredActions.filter((action) => {
      if (action.visible && typeof action.visible === "function") {
        return action.visible(item);
      }
      return true;
    });
  }, [actions, item, filterActionsByRole]);

  const items = useMemo<RowActionItem<T>[]>(() => {
    if (!rowActionContext) return [];
    // Priority order so the most-likely actions surface first, destructive last.
    return sortActionsByCategory(visibleActions).map((action) => {
      const label = typeof action.label === "function" ? action.label(item) : action.label;
      const isDisabled = Boolean(
        action.disabled && typeof action.disabled === "function" && action.disabled(item)
      );
      return {
        action,
        label,
        isDisabled,
        isDestructive: action.variant === "destructive",
        category: getActionCategory(action),
        run: () => {
          if (isDisabled || !rowActionContext) return;
          void executeDetailAction(action, rowActionContext);
        },
      };
    });
  }, [visibleActions, item, rowActionContext]);

  // Collapse zones: `top` (inline), `more` (behind a "More…" disclosure),
  // `danger` (pinned last). Consumers render the three zones; the flat `items`
  // list is kept for callers that don't collapse.
  const partition = useMemo(() => partitionByCategory(items), [items]);

  return {
    ready: Boolean(rowActionContext) && visibleActions.length > 0,
    items,
    partition,
  };
}
